import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { Rive as RiveInstance } from '@rive-app/canvas';
import {
    computeChecklistProgress,
    computeChecklistSteps,
} from 'src/app/shared/pipes/documents/checklist-completion.pipe';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaTooltipDirective } from 'src/app/shared/directives';
import { EditDataService } from '../../services/_edit-data.service';
import { EditService } from '../../services/edit-service';
import { paintChecklistDone } from './checklist-done-palette';

/** A animação de conclusão dentro do `checklist-done.riv` e onde ela termina. */
const DONE_ANIMATION = 'concluir';
const DONE_ANIMATION_END_S = 2.5;

/**
 * Barra de checklist no topo da lista de seções: "Etapa X de N", o % granular e a
 * próxima etapa. Com tudo concluído vira o cartão de conclusão, com a trilha
 * animada (Rive) e, para quem acabou de fechar a última etapa, a pergunta sobre
 * avisar os administradores. No modo recolhido vira só o % compacto. (A gestão de
 * responsáveis fica no menu do header.)
 */
@Component({
    selector: 'checklist-bar',
    templateUrl: 'checklist-bar.component.html',
    styleUrl: 'checklist-bar.component.scss',
    imports: [TranslatePipe, RaIconComponent, RaTooltipDirective],
})
export class ChecklistBarComponent {
    // * [Injects]
    public editDataService = inject(EditDataService);
    private _editService = inject(EditService);
    private _destroyRef = inject(DestroyRef);

    public isChecklistMode = computed(
        () => this.editDataService.document()?.config?.checklistMode ?? false
    );

    /** Sidebar recolhida (rail) — mostra só o % compacto. */
    public isCollapsed = this.editDataService.sessionsCollapsed;

    /** Responsável/criador/owner-admin marcam/resetam. */
    public isResponsible = this.editDataService.canCompleteChecklist;

    /** Progresso granular (pastas + seções sem pasta), ignorando assinatura. */
    public progress = computed(() =>
        computeChecklistProgress(this.editDataService.document()?.sessions)
    );

    /** As seções como etapas da trilha: "Etapa X de N" e a próxima. */
    public steps = computed(() => computeChecklistSteps(this.editDataService.document()?.sessions));

    public isComplete = computed(() => this.steps().complete);

    /**
     * Pergunta inline sobre avisar os administradores. Só para quem fechou a última
     * etapa nesta sessão (o servidor confirma pelo `checklistCompleted$`); quem abre
     * um documento já concluído vê o cartão sem a pergunta.
     */
    public askNotify = signal(false);

    private readonly _doneStage = viewChild<ElementRef<HTMLCanvasElement>>('doneStage');
    private _rive: RiveInstance | null = null;
    private _riveCanvas: HTMLCanvasElement | null = null;
    private _riveToken = 0;
    private _resizeObserver?: ResizeObserver;

    /** Estado anterior das etapas, para saber se o 100% aconteceu agora ou já veio assim. */
    private _prevTotal = 0;
    private _prevComplete = false;
    private _playOnOpen = false;

    constructor() {
        this._editService.checklistCompleted$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                this.askNotify.set(true);
                // A celebração fica no cartão da lista aberta: no rail ninguém veria.
                this.expand();
            });

        // A animação toca quando o checklist BATE 100% com o documento já na tela
        // (a última etapa acabou de ser marcada, por quem for). Um documento aberto
        // já concluído mostra a trilha parada no fim.
        effect(() => {
            const { total, complete } = this.steps();
            const canvas = this._doneStage()?.nativeElement ?? null;

            if (complete && !this._prevComplete && this._prevTotal > 0) this._playOnOpen = true;
            if (!complete) this.askNotify.set(false);
            this._prevTotal = total;
            this._prevComplete = complete;

            if (canvas) this._openAnimation(canvas);
            else this._closeAnimation();
        });

        this._destroyRef.onDestroy(() => this._closeAnimation());
    }

    public onReset(): void {
        this._editService.resetChecklist();
    }

    /** Avisa os administradores da conclusão e recolhe a pergunta. */
    public onNotify(): void {
        this.askNotify.set(false);
        this._editService.notifyChecklistCompleted();
    }

    /** Expande a lista de seções (usado ao clicar no % compacto do modo recolhido). */
    public expand(): void {
        if (this.isCollapsed()) this.editDataService.toggleSessionsCollapsed();
    }

    /**
     * Abre o `.riv` no canvas do cartão de conclusão. O runtime do Rive entra por
     * `rive-runtime` (fronteira de módulo, ver lá) e só quando o cartão aparece: a
     * lista não paga o runtime enquanto o checklist está em andamento.
     */
    private async _openAnimation(canvas: HTMLCanvasElement): Promise<void> {
        if (this._rive && this._riveCanvas === canvas) return;
        this._closeAnimation();

        this._riveCanvas = canvas;
        const token = ++this._riveToken;
        const play = this._playOnOpen;
        this._playOnOpen = false;

        const [runtime, file] = await Promise.all([
            import('src/app/shared/services/rive-runtime'),
            fetch('/rive/checklist-done.riv').then(response => response.arrayBuffer()),
        ]).catch(() => [null, null] as const);

        if (token !== this._riveToken || !runtime || !file) return;

        const { Rive, RuntimeLoader, Layout, Fit, Alignment } = runtime;
        // O .wasm vem do próprio servidor (ver `assets` no angular.json), não do CDN.
        RuntimeLoader.setWasmUrl('rive.wasm');
        RuntimeLoader.setWasmFallbackUrl('rive_fallback.wasm');

        const reducedMotion =
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

        const rive = new Rive({
            buffer: paintChecklistDone(file),
            canvas,
            autoplay: false,
            animations: DONE_ANIMATION,
            layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
            onLoad: () => {
                if (token !== this._riveToken) return;
                rive.resizeDrawingSurfaceToCanvas();
                if (play && !reducedMotion) rive.play(DONE_ANIMATION);
                else rive.scrub(DONE_ANIMATION, DONE_ANIMATION_END_S);
            },
            // Arquivo que não veio não segura nada: o cartão fica só com o texto.
            onLoadError: () => null,
        });
        this._rive = rive;

        // A superfície acompanha o tamanho do canvas (zoom, coluna redimensionada).
        this._resizeObserver = new ResizeObserver(() => rive.resizeDrawingSurfaceToCanvas());
        this._resizeObserver.observe(canvas);
    }

    private _closeAnimation(): void {
        this._riveToken++;
        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
        this._rive?.cleanup();
        this._rive = null;
        this._riveCanvas = null;
    }
}
